import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../PermissionsProvider";
import { ApplicationService } from "../../../services/ApplicationService";
import { Application } from "../../../model_gen/evaluation";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Label } from "../../ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "../../../lib/utils";

interface TestSetApplicationProps {
  application?: Application | null;
  onChange: (application: Application | null) => void;
}

const TestSetApplication: React.FC<TestSetApplicationProps> = ({
  application,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    ApplicationService.getApplications(setLoading, apiKey).subscribe(
      (response: Application[]) => {
        const list = Array.isArray(response) ? response : [];
        setApplications(list);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching applications:", error);
        setLoading(false);
      }
    );
  }, [apiKey]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground mb-2">
          Select Application
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the application for which you want to create a workflow test set. Any workflow in this application can be evaluated against the test set.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="application">Application</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={loading}
            >
              {application
                ? application.application_name
                : loading
                ? "Loading applications..."
                : applications.length === 0
                ? "No applications available"
                : "Select application..."}
              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search applications..." />
              <CommandList>
                <CommandEmpty>No applications found.</CommandEmpty>
                <CommandGroup>
                  {applications.map((app) => (
                    <CommandItem
                      key={app.id}
                      value={app.application_name || ""}
                      onSelect={() => {
                        onChange(app);
                        setOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          application?.id === app.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-gc-med">{app.application_name}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {application && (
        <Card className="border-maitai-green bg-maitai-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-maitai-green text-white rounded-full">
                <CheckIcon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-gc-med text-foreground">
                  Selected: {application.application_name}
                </div>
                <div className="text-xs text-muted-foreground">
                  All workflows in this application can be tested against this test set.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {application && (
        <div className="pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <strong>Next:</strong> You&apos;ll provide a name and description, then select workflow runs or sessions to include in the test set.
          </div>
        </div>
      )}
    </div>
  );
};

export default TestSetApplication;
